import React, { useCallback, useEffect, useMemo } from 'react';
import {
  AppButton,
  Box,
  EmptyServicesListIcon,
  Inscription,
  List,
  PaginationBar,
  SearchInput,
  Text,
  useDebounce,
  usePaginationParams,
} from '@common-fe/common-fe';

import useGetReceiptsQuery from '@/modules/employee/Employee/Documents/queries/useGetReceipts.query';
import { useGeneralInfoStore } from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/stores';
import { useFilters } from '@/utils';

import ReceiptsListItem from './ReceiptsListItem';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_CHARS_COUNT = 3;

const ReceiptsList = () => {
  const { page, perPage, setPerPage, setPage, searchString } = usePaginationParams(DEFAULT_PAGE_SIZE);

  const { filtersValues, setFilterValue } = useFilters<{ searchString: string }>(
    { searchString },
    { autoClear: true }
  );
  const debouncedSearch = useDebounce(
    filtersValues.searchString.length >= SEARCH_CHARS_COUNT ? filtersValues.searchString : ''
  );
  const { employeeId } = useGeneralInfoStore((state) => state.state);

  const { receipts, total, isLoading } = useGetReceiptsQuery({
    employeeId,
    page,
    perPage,
    searchString: debouncedSearch,
  });

  const handleView = useCallback((id?: string) => {}, []);

  const handleDelete = useCallback((id?: string) => {}, []);

  const children = useMemo(() => {
    if (isLoading) {
      return <List.Preloader testId="receipt-list_preloader" />;
    }

    if (!receipts?.length) {
      return (
            <Box
              data-testid="TransactionsNotFound"
              pad={{ top: 'spacing32' }}
              align="center"
            >
              <Box margin={{ bottom: 'spacing24' }}>
                <EmptyServicesListIcon />
              </Box>
              <Inscription
                data-testid="TransactionsNotFound-title"
                color="textDisabled"
                weight="bold"
                size="medium"
              >
                You have no Receipts added yet.
              </Inscription>
            </Box>
      );
    }

    return receipts?.map((receipt) => (
      <ReceiptsListItem
        key={receipt?.id}
        id={receipt?.id}
        fileName={receipt?.fileName}
        providerName={receipt?.providerName}
        createdAt={receipt?.createdAt}
        claimId={receipt?.claimId}
        documentId={receipt?.documentId}
        fileUrl={receipt?.fileUrl}
        onView={handleView}
        onDelete={handleDelete}
      />
    ));
  }, [isLoading, receipts, handleView, handleDelete]);

  useEffect(() => {
    setPage(1);
  }, [setPage, filtersValues.searchString]);

  return (
    <Box data-testId="receipt-list_wrapper" pad="spacing24" background="module" round="moduleRound">
      <Box direction="row" align="center" justify="between">
        <Text weight={700} data-testId="receipt-list_total">
          Files: {total}
        </Text>

        <Box gap="spacing12" direction="row" align="center">
          <Box width={{ min: '305px' }}>
            <SearchInput
              testId="receipts-list"
              value={filtersValues.searchString}
              onChange={setFilterValue.searchString}
              placeholder="Search for file name"
              hasSearchingHistory
            />
          </Box>

          <AppButton buttonType="primary" style={{ minWidth: '120px' }}>
            Upload
          </AppButton>
        </Box>
      </Box>

      <Box margin={{ top: 'spacing24' }}>
        <List>
          <List.Header>
            <List.Header.Item flex={0.6}></List.Header.Item>
            <List.Header.Item flex={1.5}>File name</List.Header.Item>
            <List.Header.Item flex={1.5}>Upload date</List.Header.Item>
            <List.Header.Item flex={1}>Linked claims</List.Header.Item>
            <List.Header.Item flex={0.4} textAlign="end">
              Actions
            </List.Header.Item>
          </List.Header>

          {children}
        </List>
      </Box>

      {!!receipts?.length && (
        <PaginationBar
          total={total}
          page={page}
          onChangePage={setPage}
          pageSize={perPage}
          onChangePageSize={setPerPage}
        />
      )}
    </Box>
  );
};

export default ReceiptsList;
